import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import { CONFIGURATION_SECTION_OPTIONS_QUERY } from "../queries";
import type { CoverageStaff, SectionConsultantAssignmentRole } from "../types";
import StaffUserAutocomplete from "./StaffUserAutocomplete";

type AssignCareerConsultantDialogProps = {
  open: boolean;
  onClose: () => void;
  onAssign: (input: { sectionId: string; backofficeUserId: string; roleType: SectionConsultantAssignmentRole }) => void;
  saving?: boolean;
  sectionId?: string;
  sectionName?: string;
  consultant?: CoverageStaff | null;
};

export default function AssignCareerConsultantDialog({
  open,
  onClose,
  onAssign,
  saving = false,
  sectionId: lockedSectionId,
  sectionName: lockedSectionName,
  consultant: lockedConsultant
}: AssignCareerConsultantDialogProps) {
  const [sectionId, setSectionId] = useState(lockedSectionId ?? "");
  const [consultant, setConsultant] = useState<CoverageStaff | null>(lockedConsultant ?? null);
  const [roleType, setRoleType] = useState<SectionConsultantAssignmentRole>("CAREER_CONSULTANT");
  const sectionLocked = Boolean(lockedSectionId);
  const consultantLocked = Boolean(lockedConsultant);

  const { data: sectionsData } = useQuery(CONFIGURATION_SECTION_OPTIONS_QUERY, {
    variables: { first: 100 },
    skip: !open
  });

  useEffect(() => {
    if (open) {
      setSectionId(lockedSectionId ?? "");
      setConsultant(lockedConsultant ?? null);
      setRoleType("CAREER_CONSULTANT");
    }
  }, [open, lockedConsultant, lockedSectionId]);

  const sectionOptions = useMemo(() => {
    const byId = new Map<string, { value: string; label: string }>();

    for (const section of sectionsData?.crmSections.nodes ?? []) {
      byId.set(section.id, { value: section.id, label: section.name });
    }

    if (lockedSectionId && lockedSectionName && !byId.has(lockedSectionId)) {
      byId.set(lockedSectionId, { value: lockedSectionId, label: lockedSectionName });
    }

    return Array.from(byId.values());
  }, [lockedSectionId, lockedSectionName, sectionsData]);

  const canSave = Boolean(sectionId && consultant && !saving);

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Assign Section Consultant"
      className="w-[560px] max-w-[95vw] max-h-[85vh]"
      hasFormElements
      disableClose={saving}
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={() => consultant && onAssign({ sectionId, backofficeUserId: consultant.id, roleType })}
            disabled={!canSave}
            loading={saving}
            testId="assign-career-consultant-save"
          >
            Assign
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <Select
          label="Section"
          searchable
          required
          disabled={sectionLocked}
          value={sectionId}
          options={sectionOptions}
          onSelect={setSectionId}
          data-testid="assign-career-consultant-section"
        />
        <StaffUserAutocomplete
          label="Consultant"
          required
          disabled={consultantLocked}
          selectedUser={consultant}
          onSelect={setConsultant}
        />
        <Select
          label="Role"
          required
          value={roleType}
          dropdownPlacement="above"
          options={[
            { value: "CAREER_CONSULTANT", label: "Career Consultant" },
            { value: "DIRECTOR", label: "Director" }
          ]}
          onSelect={(value) => setRoleType(value as SectionConsultantAssignmentRole)}
          data-testid="assign-section-consultant-role"
        />
      </div>
    </Dialog>
  );
}
